import React from 'react'
import { Box, Typography, Card, useTheme } from '@mui/material'
import { format } from 'date-fns'
import { grey } from '../../../../colors'
import { SurveyQuestionType } from '../../../../models/Survey'
import { type ReportWithComments, type Report, type KeyStatSurveyItem, type ReportsV2 } from '../../../../models/Report'
import { type SurveyCompletionRate } from '../../../../models/Division'
import { handleSurveyItems, titleFormatted } from '../../../../helpers'
import SurveyItemResponsePlaceHolder from '../SurveyItemResponsePlaceHolder'
import CommentCard from '../CommentCard'
import BarChart from '../BarChart'
import Title from '../BarChart/Title'
import { StatisticBox } from '../BarChart/StatisticBox'

interface Props {
  reports: ReportsV2
  completionRate: SurveyCompletionRate
  reportError: Record<string, any> | null
}

const SurveyReportItems: React.FC<Props> = ({ reports, completionRate, reportError }) => {
  const theme = useTheme()
  const settings = localStorage.getItem('displaySettings')
  const parsedSettings = settings ? JSON.parse(settings) : null

  const isGenericAcademicUnit = completionRate && 'divisionId' in completionRate

  let questionNumber = 0

  // Render for FreeResponse question type
  const renderFreeResponse = (report: ReportWithComments): JSX.Element => {
    const titleText = `${questionNumber + 1}. ${report.prompt}`
    questionNumber += 1
    if ('releaseDate' in report || isGenericAcademicUnit) {
      return <SurveyItemResponsePlaceHolder key={questionNumber} title={titleText} releaseTimeStamp={report?.releaseDate} />
    }
    return <CommentCard key={questionNumber} title={titleText} comments={report.comments} />
  }

  // Render for LinearScale, MultipleChoice, MultiSelect
  const renderBarChart = (report: any, surveyItem: KeyStatSurveyItem | null = null): JSX.Element => {
    const titleText = `${questionNumber + 1}. ${report.prompt}`
    questionNumber += 1

    return (
      <Box key={questionNumber} sx={{ mb: 3 }}>
        <BarChart
          chartKey={questionNumber}
          report={report}
          showPercents={parsedSettings?.chartSettings?.percentage ?? false}
          titles={reports?.titles}
          titleComponent={<Title titleText={titleText} reports={reports} surveyItem={surveyItem} calculations={surveyItem?.calculations ?? []} />}
          statisticBox={<StatisticBox calculations={surveyItem?.calculations} />}
        />
      </Box>
    )
  }

  // Render for Title question type
  const renderTitle = (report: Report): JSX.Element => titleFormatted(report.prompt)

  // Main report rendering handler
  const handleReportItem = (report: Report, surveyItem: KeyStatSurveyItem | null = null): JSX.Element => {
    switch (report.itemType) {
      case SurveyQuestionType.FreeResponse:
        return renderFreeResponse(report)
      case SurveyQuestionType.LinearScale:
      case SurveyQuestionType.MultipleChoice:
      case SurveyQuestionType.MultiSelect:
        return renderBarChart(report, surveyItem)
      case SurveyQuestionType.Title:
        return renderTitle(report)
      default:
        return <></>
    }
  }

  if (!reports || reportError) {
    const isCollectingData = reportError?.message?.earlyAccess_1ReleaseDate === null && reportError?.message?.earlyAccess_2ReleaseDate === null
    return (
      <Card>
        <Box sx={{ p: `${theme.spacing(8)} ${theme.spacing(6)}` }}>
          {isCollectingData
            ? <Typography color={grey[850]} >
              Results for this survey are currently being collected.
            </Typography>
            : <Typography component={'span'} fontWeight={500} color={grey[250]}>
              {reportError?.message}
            </Typography>
          }
        </Box>
      </Card>
    )
  }
  // Main component render
  return (
    <>
      {
        handleSurveyItems(reports.surveySections, handleReportItem)
      }
    </>
  )
}

export default SurveyReportItems
